import { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { LoginContext } from '../../context/loginContext';
import CustomModalMobileButton from '../CustomModalMobileButton';
import { getMyProfileData, saveStatus } from '../../pages/personalDetails/personalDetailsService';
import { stripHtml } from '../../pages/commonMethods/index.jsx';

const STATUS_TYPES = [
  {
    key: 'friendship',
    category: 'FRIENDSHIP',
    labelKey: 'myprofile.friendship',
    profileUrl: 'questionnaire/get/profile/friendship',
  },
  {
    key: 'partnership',
    category: 'PARTNERSHIP',
    labelKey: 'myprofile.partnership',
    profileUrl: 'questionnaire/get/profile/partnership',
  },
];

const EMPTY_STATUSES = STATUS_TYPES.reduce((values, statusType) => {
  values[statusType.key] = '';
  return values;
}, {});

const getVisibleStatusTypes = (activeStatusTypes) => {
  const visibleStatusTypes = STATUS_TYPES.filter((statusType) => activeStatusTypes?.[statusType.key]);
  return visibleStatusTypes.length ? visibleStatusTypes : STATUS_TYPES;
};

function ChangeStatusModal(props) {
  const { show, handleClose, handleShowToast, activeStatusTypes } = props;
  const [statusValues, setStatusValues] = useState(EMPTY_STATUSES);
  const [originalStatusValues, setOriginalStatusValues] = useState(EMPTY_STATUSES);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t, i18n } = useTranslation();
  const visibleStatusTypes = getVisibleStatusTypes(activeStatusTypes);

  const {
    loginData: { accessToken },
  } = useContext(LoginContext);

  const tokenLang = {
    language: i18n.language,
    accessToken,
  };

  useEffect(() => {
    if (!show) return;

    let isCancelled = false;

    const getStatusValues = async () => {
      setIsLoading(true);
      setErrorMessage('');

      const nextStatuses = { ...EMPTY_STATUSES };
      const failures = [];

      await Promise.all(
        visibleStatusTypes.map(async (statusType) => {
          try {
            const result = await getMyProfileData(tokenLang, statusType.profileUrl);
            if (result?.status === 200) {
              nextStatuses[statusType.key] = stripHtml(result?.data?.status);
              return;
            }

            if (result) {
              failures.push(result?.data?.message || t('myprofile.statusLoadError', { category: t(statusType.labelKey) }));
            }
          } catch (error) {
            failures.push(t('myprofile.statusLoadError', { category: t(statusType.labelKey) }));
          }
        })
      );

      if (!isCancelled) {
        setStatusValues(nextStatuses);
        setOriginalStatusValues(nextStatuses);
        setErrorMessage(failures.join(' '));
        setIsLoading(false);
      }
    };

    getStatusValues();

    return () => {
      isCancelled = true;
    };
  }, [show, i18n.language, accessToken, activeStatusTypes?.friendship, activeStatusTypes?.partnership]);

  const changedStatusTypes = visibleStatusTypes.filter(
    (statusType) => statusValues[statusType.key] !== originalStatusValues[statusType.key]
  );

  const handleStatusChange = (statusType, value) => {
    setStatusValues((previousStatusValues) => ({
      ...previousStatusValues,
      [statusType.key]: value,
    }));
  };

  const saveChangedStatuses = async () => {
    if (!changedStatusTypes.length) {
      handleClose();
      return;
    }

    setSaving(true);
    setErrorMessage('');

    const failedSaves = [];
    const nextOriginalStatusValues = { ...originalStatusValues };

    await Promise.all(
      changedStatusTypes.map(async (statusType) => {
        const answerBody = {
          category: statusType.category,
          status: statusValues[statusType.key],
        };

        try {
          const response = await saveStatus(answerBody, tokenLang);
          if (response?.status === 200) {
            nextOriginalStatusValues[statusType.key] = statusValues[statusType.key];
            return;
          }

          failedSaves.push(response?.data?.message || t('myprofile.statusSaveError', { category: t(statusType.labelKey) }));
        } catch (error) {
          failedSaves.push(t('myprofile.statusSaveError', { category: t(statusType.labelKey) }));
        }
      })
    );

    setOriginalStatusValues(nextOriginalStatusValues);
    setSaving(false);

    if (failedSaves.length) {
      setErrorMessage(failedSaves.join(' '));
      return;
    }

    handleShowToast(t('member.personaldata.personaldetails.save_notification'));
    handleClose();
  };

  return (
    <Modal className="status_editor_modal" show={show} onHide={handleClose} backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Modal.Header closeLabel={t('common.close')}>
        <div className="profile_logo_name">
          <div className="profile_info">
            <h3>
              <FontAwesomeIcon icon={faUser} /> {t('member.header.changeMyStatus')}
            </h3>
          </div>
        </div>
        <CustomModalMobileButton loading={saving} onShowHide={handleClose} />
      </Modal.Header>
      <Modal.Body>
        {errorMessage ? (
          <Alert className="member_address mt-2" variant="danger" id="main_alert" dismissible onClose={() => setErrorMessage('')}>
            <p>{errorMessage}</p>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="status_editor_loader">
            <Spinner />
          </div>
        ) : (
          <>
            <p className="status_editor_hint">{t('myprofile.statusEditorDescription')}</p>
            <div className="status_editor_fields">
              {visibleStatusTypes.map((statusType) => (
                <Form.Group className="text_question status_editor_field" key={statusType.key} controlId={`${statusType.key}-status`}>
                  <Form.Label>{t(statusType.labelKey)}</Form.Label>
                  <Form.Control
                    as="textarea"
                    value={statusValues[statusType.key]}
                    onChange={(event) => handleStatusChange(statusType, event.target.value)}
                    disabled={saving}
                  />
                </Form.Group>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="status_editor_actions">
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={saveChangedStatuses} disabled={isLoading || saving || !changedStatusTypes.length}>
          {saving ? <Spinner size="sm" /> : t('member.incompletequestionnaire.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ChangeStatusModal;